"use client"

import { parseFolderIdFromPath } from "@/lib/utils"
import { useDomainStore } from "@/stores/use-domain-store"
import { AppstoreOutlined, BarsOutlined, UploadOutlined } from "@ant-design/icons"
import { Button, Modal, Select, Space, Spin, Upload, UploadFile, message } from "antd"
import Title from "antd/es/typography/Title"
import { debounce } from "lodash"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import React, { useCallback, useEffect, useState } from "react"
import { t } from "../../../../languages/i18n"
import { MediaData } from "../../../../lib/config/types"
import { addMedia } from "../services/apiServices"
import { resolveMediaDomainParam } from "../utils/media-domain"
import FolderSelector from "./FolderSelector"
import MediaList from "./MediaList"
import SearchInput from "./SearchInput"

const { Dragger } = Upload

// Sort dropdown (modal picker + grid trang thư viện). Feed thẳng vào API sort
// của useMediaList qua prop sortValue.
const SORT_OPTIONS = [
  { value: "createdAt:desc", label: t("Mặc định") },
  { value: "createdAt:asc", label: t("Ngày tạo - ASC") },
  { value: "name:asc", label: t("Tên - ASC") },
  { value: "name:desc", label: t("Tên - DESC") },
  { value: "size:asc", label: t("Kích thước - ASC") },
  { value: "size:desc", label: t("Kích thước - DESC") },
]

interface MediaProp {
  isOpenModal?: boolean
  /** "single" → chọn 1 ảnh (xác nhận OK); "multi" → chọn nhiều rồi bấm "Select N". */
  selectMode?: "single" | "multi"
  // Ở multi, callback nhận mảng lúc runtime; handler multi khai báo (media|media[])
  // vẫn gán được vào type single này (tham số rộng hơn), nên giữ để không phá caller cũ.
  onSelectMedia?: (media: MediaData) => void
}

const Media = ({ isOpenModal, selectMode = "single", onSelectMedia }: MediaProp) => {
  // Mặc định hiển thị dạng lưới (grid); bấm nút Bars để chuyển sang bảng/list.
  // Trong modal picker, optionView=true nghĩa là list-rows (không phải antd Table).
  const [optionView, setOptionView] = useState(false)
  // Sort dropdown (single source cho prop sortValue). Modal luôn dùng; trang thư
  // viện dùng khi ở grid (table mode đã có sorter trên header cột).
  const [sortValue, setSortValue] = useState("createdAt:desc")
  // Dropdown sort hiển thị ở modal, hoặc ở trang thư viện khi đang grid (!optionView).
  const showSortSelect = isOpenModal || !optionView
  const [totalItems, setTotalItems] = useState<number>(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const router = useRouter()
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const domain = resolveMediaDomainParam(selectedDomain)

  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Folder hiện tại lấy từ URL (?folderPath) để nó là nguồn sự thật: back/forward
  // trình duyệt đổi param mà không remount, nên phải đồng bộ state theo param —
  // nếu không sẽ kẹt ở folder con dù URL đã về root.
  const urlFolderId = parseFolderIdFromPath(isOpenModal ? null : searchParams.get("folderPath"))
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(urlFolderId)

  useEffect(() => {
    if (isOpenModal) return
    setCurrentFolderId(prev => (prev === urlFolderId ? prev : urlFolderId))
  }, [urlFolderId, isOpenModal])
  // In modal mode keep search in local state so it never leaks into the host
  // page URL (e.g. category edit → list would then filter by this term).
  const [modalSearchTerm, setModalSearchTerm] = useState("")
  // SearchInput giữ value nội bộ (chỉ đọc defaultValue lúc mount) → đổi key để
  // reset ô nhập khi xoá từ khoá từ bên ngoài (mở folder từ kết quả tìm kiếm).
  const [searchInputKey, setSearchInputKey] = useState(0)
  const currentSearchTerm = isOpenModal ? modalSearchTerm : searchParams.get("name") || ""

  // Upload nằm ngay trong trang (modal) thay vì điều hướng sang /upload/create —
  // giữ nguyên folder đang đứng, không mất vị trí trong thư viện.
  const [uploadOpen, setUploadOpen] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [uploading, setUploading] = useState(false)

  const handleUploadRequest = async (options: any) => {
    const { file, onSuccess, onError } = options
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("files", file)
      // Upload thẳng vào folder hiện tại; null = thư viện gốc.
      const items = await addMedia(formData, currentFolderId, domain)
      if (Array.isArray(items) && items.length > 0) {
        onSuccess(items)
        setRefreshKey(k => k + 1)
        // Picker chọn 1 ảnh (vd. ảnh đại diện bài viết): tải lên xong là chọn
        // luôn, khỏi phải bấm "Xong" rồi quay lại grid chọn tiếp ảnh vừa tải.
        if (selectMode === "single" && onSelectMedia) {
          onSelectMedia(items[0])
          setUploadOpen(false)
          setFileList([])
        }
      } else {
        onError(new Error("Upload failed or returned no files"))
        message.error(t("Failed to upload media."))
      }
    } catch (error) {
      console.error("[Upload] error:", error)
      onError(error)
      message.error(t("Failed to upload media."))
    } finally {
      setUploading(false)
    }
  }

  const handleSearchChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {

      const cleanedValue = e.target.value.trim().replace(/\s+/g, " ").toLowerCase();

      if (isOpenModal) {
        setModalSearchTerm(cleanedValue);
        return;
      }

      const params = new URLSearchParams(searchParams.toString());

      if (cleanedValue) {

        params.set("name", cleanedValue);
      } else {

        params.delete("name");
      }


      params.delete("page");


      router.push(`${pathname}?${params.toString()}`);
    }, 500),
    [pathname, router, searchParams, isOpenModal],
  )

  const handleFolderChange = useCallback((folderId: number | null) => {
    setCurrentFolderId(folderId)
    setRefreshKey(k => k + 1)
    if (isOpenModal) return
    const params = new URLSearchParams(searchParams.toString())
    if (folderId) {
      params.set("folderPath", `/${folderId}`)
    } else {
      params.delete("folderPath")
    }
    router.push(`${pathname}?${params.toString()}`)
  }, [pathname, router, searchParams, isOpenModal])

  // Mở folder từ kết quả tìm kiếm: vào folder + xoá từ khoá (nếu giữ, danh sách
  // file vẫn là kết quả search toàn thư viện chứ không phải nội dung folder).
  const handleOpenFolderFromSearch = useCallback(
    (folderId: number) => {
      handleSearchChange.cancel()
      setSearchInputKey(k => k + 1)
      setCurrentFolderId(folderId)
      setRefreshKey(k => k + 1)
      if (isOpenModal) {
        setModalSearchTerm("")
        return
      }
      const params = new URLSearchParams(searchParams.toString())
      params.delete("name")
      params.delete("page")
      params.set("folderPath", `/${folderId}`)
      router.push(`${pathname}?${params.toString()}`)
    },
    [handleSearchChange, pathname, router, searchParams, isOpenModal],
  )

  return (
    <div className="p-4 pt-0">
      {!isOpenModal && (
        <div className="flex justify-between space-x-4 items-center mt-4">
          <div className="flex items-baseline gap-3">
            <Title level={2} className="!m-0">
              {t("Media Library")}
            </Title>
            {/* <span className="text-xs text-[var(--admin-muted-fg)]">
              {domain ? `${t("Domain")}: ${domain}` : `${t("Domain")}: ${t("All Domains")}`}
            </span> */}
          </div>
        </div>
      )}
      <FolderSelector
        currentFolderId={currentFolderId}
        onFolderChange={handleFolderChange}
        refreshTrigger={refreshKey}
        onAddAssets={() => setUploadOpen(true)}
        searchTerm={currentSearchTerm}
        onOpenSearchResult={handleOpenFolderFromSearch}
      />
      <div className="lg:flex lg:space-x-0 justify-between align-middle mt-5 py-3 px-4 border-[0.5px] border-[#a5a1a18e] rounded-lg">
        <Space>
          {/* Toggle grid/list hiện ở CẢ modal picker (khôi phục list view của modal cũ). */}
          <Space className="view">
            <Button
              color={`${optionView ? "primary" : "default"}`}
              variant={`${optionView ? "solid" : "outlined"}`}
              onClick={() => setOptionView(true)}
            >
              <BarsOutlined />
            </Button>
            <Button
              color={`${!optionView ? "primary" : "default"}`}
              variant={`${!optionView ? "solid" : "outlined"}`}
              onClick={() => setOptionView(false)}
            >
              <AppstoreOutlined />
            </Button>
          </Space>
          {showSortSelect && (
            <Select
              value={sortValue}
              onChange={setSortValue}
              options={SORT_OPTIONS}
              style={{ width: 170 }}
            />
          )}
        </Space>
        <SearchInput
          key={searchInputKey}
          handleSearchChange={handleSearchChange}
          defaultValue={currentSearchTerm}
        />
      </div>
      <MediaList
        key={refreshKey}
        searchTerm={currentSearchTerm}
        folderId={currentFolderId}
        isOpenModal={isOpenModal}
        // Modal không dùng antd Table (isView=false); list view của picker do
        // modalListView điều khiển. Trang thư viện vẫn dùng optionView cho Table/grid.
        isView={isOpenModal === true ? false : optionView}
        modalListView={isOpenModal === true ? optionView : false}
        // Dropdown là control sort khi hiển thị (modal, hoặc grid trang thư viện);
        // ở table mode trang thư viện để undefined cho sorter header + Configure view.
        sortValue={showSortSelect ? sortValue : undefined}
        selectMode={selectMode}
        onSelectMedia={onSelectMedia as ((media: MediaData | MediaData[]) => void) | undefined}
        onBulkActionComplete={() => setRefreshKey(k => k + 1)}
      />

      <Modal
        title={t("Thêm hình ảnh")}
        open={uploadOpen}
        onCancel={() => {
          if (uploading) return
          setUploadOpen(false)
          setFileList([])
        }}
        footer={[
          <Button
            key="done"
            type="primary"
            disabled={uploading}
            onClick={() => {
              setUploadOpen(false)
              setFileList([])
            }}
          >
            {t("Xong")}
          </Button>,
        ]}
        destroyOnClose
        maskClosable={!uploading}
      >
        <Dragger
          // Chỉ giới hạn 1 file khi đang làm PICKER chọn 1 ảnh (single + có
          // onSelectMedia) để tải lên xong auto-chọn. Trang thư viện standalone
          // không có onSelectMedia → luôn cho phép bulk upload nhiều file.
          multiple={!(selectMode === "single" && !!onSelectMedia)}
          fileList={fileList}
          customRequest={handleUploadRequest}
          onChange={info => {
            setFileList(info.fileList)
            const allDone =
              info.fileList.length > 0 && info.fileList.every(f => f.status === "done")
            if (allDone) {
              setRefreshKey(k => k + 1)
              message.success(t("Media uploaded successfully!"))
            }
          }}
          className="border-dashed border border-gray-300 rounded-lg"
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">{t("Click or drag file to this area to upload")}</p>
          <p className="ant-upload-hint">{t("Support for a single or bulk upload.")}</p>
          {uploading && (
            <div className="mt-2">
              <Spin size="small" />
            </div>
          )}
        </Dragger>
      </Modal>
    </div>
  )
}

export default Media
